<template>
  <v-data-table-server
    :items-per-page="25"
    :items-per-page-options="[{ value: 25, title: '25' }]"
    v-model:page="page"
    :items-length="count"
    no-filter
    :hover="chits.length > 0"
    item-value="id"
    :headers="chitDataTableHeaders"
    :items="chits"
    :loading="loading"
    color="primary"
    sticky
    fixed-header
    density="comfortable"
    height="calc(100% - 37px)"
    style="height: calc(100% - 93px); width: 100%"
    :row-props="generateRowProps"
  >
    <template #[`item.chit_types`]="{ value }">
      <span class="text-subtitle-2 text-weight-bold">{{ value.join(', ') }}</span>
    </template>

    <template #[`item.guards`]="{ value }">
      <span class="text-subtitle-2 text-weight-bold">{{
        value.map((guard: IEmployee) => guard.getFullName()).join(', ')
      }}</span>
    </template>

    <template v-slot:[`item.state`]="{ value }">
      <StatusChip
        :color="getChitVisualByState(value)?.color"
        :text="getChitVisualByState(value)?.text"
      >
      </StatusChip>
    </template>

    <template #[`item.actions`]="{ item }">
      <ChitDetailOptionsButton
        :state="item.state"
        :chit="item"
        @export-chit-pdf-pressed="() => emit('export-chit-pdf-pressed', item)"
        @edit-chit-pressed="() => emit('edit-chit-pressed', item)"
        @delete-chit-pressed="() => emit('delete-chit-pressed', item)"
      />
    </template>
    <template v-slot:no-data>
      <NoDataAvailablePlaceholder
        width="400"
        header="No chits available"
        header-class="font-weight-medium text-h5"
      >
        <template #description>
          <p class="mt-0 mb-6 text-caption text-medium-emphasis">
            <span v-if="currentUser?.hasPermission('chits.add_chit')">
              <template>
                It looks like there are currently no chits in the chit list. Get started by adding
                new chits here to build your chit list.</template
              >
            </span>
            <span v-else
              >You do not have the permission to create a chit. Chit your administrator for more
              details
            </span>
          </p>
        </template>

        <template #primaryAction>
          <v-btn
            color="primary"
            depressed
            v-if="currentUser?.hasPermission('chits.add_chit')"
            @click="emit('chit-create-pressed')"
          >
            <template #prepend>
              <v-icon left class="hidden-sm-and-down"> {{ route.meta.icon }} </v-icon>
            </template>
            Create Chit
          </v-btn>
        </template>
      </NoDataAvailablePlaceholder>
    </template>
  </v-data-table-server>
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { format } from 'date-fns'

import type { IChit } from '@/models/chit'
import { useAuthStore, useChitStore } from '@/stores'

import NoDataAvailablePlaceholder from '@/components/common/NoDataAvailablePlaceholder.vue'
import ChitDetailOptionsButton from './ChitActionsMenuBtn.vue'
import type { IEmployee } from '@/models/employee'

interface Props {
  chits: IChit[]
  count: number
  loading: boolean
}

interface Emit {
  (e: 'chit-selected', chit: IChit): void
  (e: 'export-chit-pdf-pressed', chit: IChit): void
  (e: 'edit-chit-pressed', chit: IChit): void
  (e: 'chit-create-pressed'): void
  (e: 'delete-chit-pressed', chit: IChit): void
}

const page = defineModel<number>('page')

const emit = defineEmits<Emit>()
defineProps<Props>()

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

const { getChitVisualByState } = useChitStore()

const route = useRoute()

const chitDataTableHeaders = ref<any[]>([
  {
    title: '#',
    align: 'start',
    key: 'chit_number',
    width: 20,
    sortable: false
  },
  { title: 'Chit Types', value: 'chit_types' },
  { title: 'Client', value: 'client' },
  {
    title: 'Location',
    value: 'site.address.address1'
  },
  { title: 'Created By', value: 'created_by' },
  { title: 'Guards', value: 'guards' },
  {
    title: 'Last Updated',
    key: 'modified',
    value(item: IChit) {
      return format(item.modified! as Date, 'MMMM dd, yyyy - HH:mm')
    },
    sortable: false
  },
  {
    title: 'Received At',
    key: 'received_at',
    value(item: IChit) {
      return item.chit_detail.received_at
        ? format(item.chit_detail.received_at, 'MMMM dd, yyyy - HH:mm')
        : '-'
    },
    sortable: false
  },
  {
    title: 'Arrived At',
    key: 'arrived_at',
    value(item: IChit) {
      return item.chit_detail.arrived_at
        ? format(item.chit_detail.arrived_at, 'MMMM dd, yyyy - HH:mm')
        : '-'
    },
    sortable: false
  },
  {
    title: 'Departed At',
    key: 'departed_at',
    value(item: IChit) {
      return item.chit_detail.departed_at
        ? format(item.chit_detail.departed_at!, 'MMMM dd, yyyy - HH:mm')
        : '-'
    },
    sortable: false
  },
  { title: 'State', value: 'state' },
  { title: 'Approved by', value: 'approver' },
  { title: '', value: 'actions' }
])

function generateRowProps(props: { index: number; item: IChit }) {
  return {
    // apply click listener to row props to open detail drawer
    onClick: () => emit('chit-selected', props.item)
  }
}
</script>
